import { type Draft, type Objectish, type Patch, produce } from "immer";

import {
	type ArchiveCanvasOperation,
	type ArchivePageOperation,
	type CreatePageOperation,
	type DuplicatePageOperation,
	type PageInfo,
	type SetCanvasTemplateOperation,
	type SetCanvasTitleOperation,
	type SetPageOrderOperation,
	type SetPageTitleOperation,
} from "../api-canvas-metadata";
import { type DocumentMetadata, type PageMetadata } from "../api-types";
import { Transaction } from "../metadata-client";

export type DoTransactionFn<TState extends Objectish> = (draft: Draft<TState>) => void;

const pagesAddedOrRemoved = (pages: PageMetadata[], canvasMetadata: DocumentMetadata) => {
	const newPages = new Set(pages.map((page) => page.id));
	const currentPages = new Set(canvasMetadata.pagesMetadata.map((page) => page.id));

	const result = new Set([...newPages, ...currentPages]);
	return result.size !== newPages.size || result.size !== currentPages.size;
};

const getPagesFromNewPageOrder = (
	newPageIDs: PageInfo[],
	canvasPages: PageMetadata[]
): PageMetadata[] => {
	// Make map of page ID -> parent page ID, for all pages with a parent page.
	const parentPageIds = newPageIDs.reduce((acc, { pageId, parentPageId }) => {
		if (parentPageId !== undefined && parentPageId !== null) {
			acc.set(pageId, parentPageId);
		}
		return acc;
	}, new Map<string, string>());

	// Set of page IDs that are in the supplied new page order.
	const uniqueNewPageIDs = new Set(newPageIDs.map((page) => page.pageId));

	// Pages not referenced in the page ordering, usually these are archived pages.
	const unreferencedPageIDs = new Set(
		canvasPages.map((page) => page.id).filter((id) => !uniqueNewPageIDs.has(id))
	);

	// Generate the array of fully-materialzed pages to provide to the server.
	const reorderedPageIDs = [...uniqueNewPageIDs, ...unreferencedPageIDs];
	const reorderedPages = reorderedPageIDs.reduce((acc, pageID) => {
		const page = canvasPages.find((pageMetadata) => {
			return pageMetadata.id === pageID;
		});
		if (page === undefined) {
			return acc;
		}
		page.parentPageID = parentPageIds.get(page.id) ?? null;
		return [...acc, page];
	}, [] as PageMetadata[]);

	return reorderedPages;
};

const makeTransaction = (
	fn: DoTransactionFn<DocumentMetadata>,
	canvasMetadataSnapshot: DocumentMetadata
): Transaction<DocumentMetadata> => {
	const patches: Patch[] = [];
	const inversePatches: Patch[] = [];
	const newState = produce<DocumentMetadata>(
		canvasMetadataSnapshot,
		fn,
		(_patches, _inversePatches) => {
			patches.push(..._patches);
			inversePatches.push(..._inversePatches);
		}
	);
	return new Transaction(newState, patches, inversePatches);
};

// Transaction and operations for the metadata object.
// Used by both the metadata client and metadata server.

export const setCanvasTitleTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	newTitle: string
) => {
	const fn = (draft) => {
		draft.title = newTitle;
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const setCanvasTitleOperation = (newTitle: string): SetCanvasTitleOperation => ({
	operation: "setCanvasTitle",
	newTitle,
});

export const archiveCanvasTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	archive: boolean
) => {
	const fn = (draft) => {
		draft.archived = archive;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const archiveCanvasOperation = (archive: boolean): ArchiveCanvasOperation => ({
	operation: "archiveCanvas",
	archived: archive,
});

export const setCanvasTemplateTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	isTemplate: boolean
) => {
	const fn = (draft) => {
		draft.isTemplate = isTemplate;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const setCanvasTemplateOperation = (isTemplate: boolean): SetCanvasTemplateOperation => ({
	operation: "setCanvasTemplate",
	isTemplate,
});

export const setPageTitleTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	pageId: string,
	title: string,
	slug?: string
) => {
	const fn = (draft) => {
		draft.pagesMetadata.find((page) => {
			if (page.id === pageId) {
				page.title = title;
				if (slug) {
					page.slug = slug;
				}
			}
		});
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const setPageTitleOperation = (pageId: string, newTitle: string): SetPageTitleOperation => ({
	operation: "setPageTitle",
	pageId,
	newTitle,
});

export const setPageOrderTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	pageReorderInfo: PageInfo[]
) => {
	const fn = (draft) => {
		const newPages = getPagesFromNewPageOrder(pageReorderInfo, draft.pagesMetadata);
		// Prevents adding/deleting pages during reorder, which would return an error
		// from the server.
		if (!pagesAddedOrRemoved(newPages, draft)) {
			draft.pagesMetadata = newPages;
		}
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const setPageOrderOperation = (pageReorderInfo: PageInfo[]): SetPageOrderOperation => {
	return {
		operation: "setPageOrder",
		newPageIds: pageReorderInfo,
	};
};

export const createPageTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	authorId: string,
	title: string,
	pageId?: string,
	slug?: string
) => {
	const fn = (draft) => {
		if (!draft.pagesMetadata) {
			draft.pagesMetadata = [];
		}

		const newPage: PageMetadata = {
			id: pageId ?? "",
			title,
			authorID: authorId,
			archived: false,
			slug: slug ?? "",
			parentPageID: null,
		};
		draft.pagesMetadata.push(newPage);
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const createPageOperation = (authorId: string, title: string): CreatePageOperation => ({
	operation: "createPage",
	authorId,
	title,
});

export const archivePageTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	pageId: string,
	archived: boolean
) => {
	const fn = (draft) => {
		const page = draft.pagesMetadata.find((page) => page.id === pageId);
		if (!page) {
			throw new Error(`Could not find page with ID ${pageId}`);
		}
		if (page.archived === archived) {
			// page arhive is already set to the right value.
			return;
		}

		// Only traverse and modify children pages when archive is true.
		// When archive is false, it might unarchive/revive previously
		// archived child pages.
		if (archived === true) {
			draft.pagesMetadata.forEach((pageMetadata) => {
				if (pageMetadata.parentPageID === page.id) {
					pageMetadata.archived = true;
				}
			});
		}

		// Archive the page.
		page.archived = archived;
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const archivePageOperation = (pageId: string, archived: boolean): ArchivePageOperation => ({
	operation: "archivePage",
	pageId,
	archived,
});

export const duplicatePageTransaction = (
	canvasMetadataSnapshot: DocumentMetadata,
	authorId: string,
	sourcePageId: string,
	pageId?: string,
	title?: string,
	slug?: string
) => {
	const fn = (draft) => {
		const pageIndex = draft.pagesMetadata.findIndex((page) => page.id === sourcePageId);
		if (pageIndex < 0) {
			throw new Error(`Could not find page with ID ${sourcePageId}`);
		}
		const duplicatePage: PageMetadata = {
			id: pageId ?? "",
			title: title ?? "",
			slug: slug ?? "",
			authorID: authorId,
			archived: false,
			parentPageID: null,
		};
		draft.pagesMetadata.splice(pageIndex + 1, 0, duplicatePage);
		return draft;
	};
	return makeTransaction(fn, canvasMetadataSnapshot);
};
export const duplicatePageOperation = (
	authorId: string,
	sourcePageId: string
): DuplicatePageOperation => ({
	operation: "duplicatePage",
	sourcePageId,
	authorId,
});
